// react
import React, { useEffect, useState } from "react";
// redux
import { useSelector } from "react-redux";

import { faEye, faEyeSlash } from "@fortawesome/pro-regular-svg-icons";
// dependencies
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import { AdminContainer, AdminTitle } from "../../../components/Admin";
import Button from "../../../components/Button";
import Chip from "../../../components/Chip";
import Toggle from "../../../components/Inputs/ToggleSwitches/ThumbSwitch";
import PageTitle from "../../../components/PageTitle";
// components
import Spinner from "../../../components/Spinner";
import ComponentLoader from "../../../components/Spinner/ComponentLoader";
import Text from "../../../components/Text";
import DisplaySftpInfoModal from "./DisplaySftpInfoModal";
import RemoveConfirmationModal from "./RemoveConfirmationModal";

import ErrorPage from "../../../pages/Error";

import { useCompanyFunction } from "../../../api/hooks";
import { useFeatureFlagQuery } from "../../../hooks/useFeatureFlag";
// hooks
import useRequest from "../../../hooks/useRequest";

// selectors
import { getUserCompanyID, getUserCompanyName } from "../../../selectors/app";

// utils
import get from "../../../utils/GetNested";

// styles
import css from "./Sftp.module.scss";

// actions

const SftpSettings = () => {
	const [sftpIntegrations, setSftpIntegrations] = useState([]);
	const [passwordType, setPasswordType] = useState("password");
	const [isRemoveModalOpen, setIsRemoveModalOpen] = useState(false);
	const [selectedIntegration, setSelectedIntegration] = useState("");
	const [isInfoModalOpen, setIsInfoModalOpen] = useState(false);
	const [url, setUrl] = useState("");

	const companyID = useSelector((state) => getUserCompanyID(state));
	const company = useSelector((state) => getUserCompanyName(state));

	// Function and feature flag queries for SFTP integration
	const { companyFunction: sftpIntegrationFunctionState, isLoading: isLoadingCompanyFunction } =
		useCompanyFunction("integrationSftp");
	const sftpIntegrationFunctionEnabled = sftpIntegrationFunctionState === "enabled";
	const {
		value: ffSftpIntegrationEnabled,
		query: { isLoading: isLoadingFeatureFlag },
	} = useFeatureFlagQuery("ff-is-enabled-sftp-integration", false);

	const { data: sftpIntegrationsData, revalidate: getSftpIntegrations } = useRequest(
		"getSftpIntegrations",
		{},
		{ skipCache: true }
	);

	const {
		data: sftpPasswordData,
		isRequesting: requestingPassword,
		revalidate: revalidateSftpPassword,
	} = useRequest("getSftpPassword", {}, { skipCache: true });

	const { revalidate: setSftpPassword } = useRequest("setSftpPassword", {}, { manual: true });

	const { revalidate: editSftpIntegration } = useRequest("editSftpIntegration", {}, { manual: true });

	const { revalidate: disableSftpPassword } = useRequest("disableSftpPassword", {}, { manual: true });

	useEffect(() => {
		if (sftpIntegrationsData && sftpIntegrationsData.results) {
			setSftpIntegrations(sftpIntegrationsData.results);
		}
	}, [JSON.stringify(sftpIntegrationsData)]);

	const handleSetSftpPassword = async () => {
		await setSftpPassword();
		revalidateSftpPassword();
	};

	const handleDisableSftpPassword = async () => {
		await disableSftpPassword();
		revalidateSftpPassword();
	};

	const handlePasswordType = () => {
		if (passwordType === "text") {
			setPasswordType("password");
		} else {
			setPasswordType("text");
		}
	};

	const generateSftpUrl = (integrationID) => `sftp.performyard.com/${companyID}/${integrationID}/`;

	const handleCloseRemoveModal = () => {
		setIsRemoveModalOpen(false);
		getSftpIntegrations();
	};

	const handleAddSftpIntegration = () => {
		window.location = "/administration/sftp/create/";
	};

	const handleEditIntegration = (ii) => {
		const integration = sftpIntegrations[ii];
		const integrationID = get(integration, ["_id"], null);

		if (integrationID) {
			window.location = `/administration/sftp/edit/${integrationID}/`;
		}
	};

	const handleIntegrationStatus = async (ii, status) => {
		// get the id of the integration being updated
		const integrationID = get(sftpIntegrations, [ii, "_id"], "");

		// determine the new status
		const newStatus = status ? "enabled" : "disabled";

		// eagerly update the state to reflect the change
		const newIntegrationState = [...sftpIntegrations];
		newIntegrationState[ii].status = newStatus;
		setSftpIntegrations(newIntegrationState);

		// make the Edit request and refetch all data
		await editSftpIntegration({
			filters: { payload: { status: newStatus }, integrationID },
		});

		await getSftpIntegrations();
	};

	const handleDeleteIntegration = (ii) => {
		setSelectedIntegration(sftpIntegrations[ii]);
		setIsRemoveModalOpen(true);
	};

	const handleShowDetails = (integrationID) => {
		const sftpUrl = generateSftpUrl(integrationID);
		setUrl(sftpUrl);
		setIsInfoModalOpen(true);
	};

	// Show a loading spinner while the feature flag is being fetched
	if (isLoadingCompanyFunction || isLoadingFeatureFlag) {
		return <ComponentLoader />;
	}

	// Only show the error page after we've confirmed the feature flag is disabled
	if (!sftpIntegrationFunctionEnabled || !ffSftpIntegrationEnabled) {
		return <ErrorPage code={403} />;
	}

	const viewPassword = (
		<div>
			<Text size="sub-heading" weight="semi-bold">
				Password
			</Text>
			<div className={css.passwordAndShow}>
				<div>
					<input
						autoComplete="password"
						type={passwordType}
						value={sftpPasswordData ? get(sftpPasswordData, ["results", "sftp", "password"], "") : ""}
						onChange={() => {}}
						className={css.input}
						data-testid="sftpPasswordInput"
						readOnly
					/>
				</div>
				{passwordType === "password" ? (
					<div className={css.visibilityIcon} onClick={handlePasswordType}>
						<FontAwesomeIcon data-testid={faEye?.iconName} icon={faEye} />
					</div>
				) : (
					<div className={css.visibilityIcon} onClick={handlePasswordType}>
						<FontAwesomeIcon data-testid={faEyeSlash?.iconName} icon={faEyeSlash} />
					</div>
				)}
				<Button color="blue" style={{ height: "37px", marginRight: "7px" }} onClick={handleSetSftpPassword}>
					Update Password
				</Button>
				<Button color="red" style={{ height: "37px" }} onClick={handleDisableSftpPassword}>
					Disable Password
				</Button>
			</div>
		</div>
	);

	return (
		<AdminContainer>
			<div className={css.sftpContainer}>
				<PageTitle title={`SFTP Setup - ${company} - PerformYard`} />
				<AdminTitle>SFTP Setup</AdminTitle>

				<div className={css.sftpPasswordContainer}>
					{!requestingPassword ? (
						<div>
							{sftpPasswordData &&
							get(sftpPasswordData, ["results", "sftp", "password"], null) !== null ? (
								viewPassword
							) : (
								<Button color="blue" style={{ height: "37px" }} onClick={handleSetSftpPassword}>
									Generate Sftp Password
								</Button>
							)}
						</div>
					) : (
						<div className={css.spinner}>
							<Spinner size={30} />
						</div>
					)}
				</div>

				<Text size="sub-heading" weight="semi-bold">
					SFTP Integrations
				</Text>

				{sftpIntegrations && sftpIntegrations.length < 10 ? (
					<Button onClick={handleAddSftpIntegration} style={{ height: "37px" }} color="blue">
						Add SFTP Integration
					</Button>
				) : null}
				{sftpIntegrations ? (
					<div className={css.sfptIntegrationsTableContainer}>
						<div className={css.columnLabels}>
							<div className={css.column}>Integration</div>
							<div className={css.column}>Enabled</div>
							<div className={css.column} />
							<div className={css.column} />
						</div>

						<div className={css.sftpIntegrationsTable}>
							{sftpIntegrations.map((item, ii) => {
								const id = item._id;
								const isEnabled = item.status === "enabled";
								const isTestingMode = item.is_testing === true;

								return (
									<div className={css.sftpObject} key={id}>
										<div className={css.integrationInfo}>
											<div
												className={`${css.cell} ${css.label}`}
												onClick={() => handleShowDetails(item._id)}
											>
												{get(item, ["name"], "")}
												{isTestingMode ? (
													<Chip
														className={css.testingChip}
														label="Testing Mode"
														size="small"
														color="orange"
													/>
												) : null}
											</div>

											<div
												data-testid="integrationToggleWrapper"
												className={`${css.cell} ${css.state}`}
											>
												<Toggle
													isLoading={false}
													onChange={(value) => handleIntegrationStatus(ii, value)}
													width={60}
													value={isEnabled}
												/>
											</div>
											<div className={`${css.cell} ${css.controls}`}>
												<Button
													color="grey"
													className={css.editButton}
													onClick={() => handleEditIntegration(ii)}
												>
													{" "}
													Edit
												</Button>
											</div>
											<div className={`${css.cell} ${css.controls}`}>
												<Button
													testid={`deleteIntegrationButton` + id}
													style={{
														display: "inline-block",
														margin: "0 3px",
														width: "40px",
														minWidth: "0",
													}}
													color="red"
													onClick={() => handleDeleteIntegration(ii)}
												>
													<FontAwesomeIcon icon={["fas", "trash"]} />
												</Button>
											</div>
										</div>
									</div>
								);
							})}
						</div>
					</div>
				) : null}
				{isRemoveModalOpen ? (
					<RemoveConfirmationModal
						handleClose={handleCloseRemoveModal}
						integrationToDelete={selectedIntegration}
					/>
				) : null}
				{isInfoModalOpen ? (
					<DisplaySftpInfoModal handleClose={() => setIsInfoModalOpen(!isInfoModalOpen)} url={url} />
				) : null}
			</div>
		</AdminContainer>
	);
};

export default SftpSettings;
